import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';
import { getAdaptiveRoleTaxonomyValue } from '@revelio/iso-utility';

import { useSelectionListState } from '../../../../shared/selection-lists/hooks/useSelectionListState';
import type { SelectionList } from '../../../../shared/selection-lists/types';
import {
  PreferenceScope,
  UserPreferenceKey,
} from '../../../../shared/user-preferences/types';
import { PreferenceBrowserStorage } from '../../../../shared/user-preferences/utils/preference-session-storage';
import type { RevelioPageId } from '../../components/PageConfigProvider/types';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import type { FilterConsistencyState } from '../engine/filterGroupConsistency';
import type { FilterAction } from '../engine/filterReducer';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';

export const useAdaptiveRoleTaxonomy = ({
  primaryFilterId,
  revelioPageId,
  filterState,
  dispatch,
}: {
  primaryFilterId?: PrimaryFilterId;
  revelioPageId?: RevelioPageId;
  filterState: FilterConsistencyState;
  dispatch: React.Dispatch<FilterAction>;
}): { isResolved: boolean } => {
  const isCompanyPage = primaryFilterId === FilterId.COMPANY;

  const { selectionLists } = useSelectionListState();

  // Tracks the taxonomy value we last dispatched or confirmed.
  // null = haven't resolved yet. When the effect dispatches a new value,
  // it sets this to that value. On the next render, filterState will
  // contain the dispatched value and the effect will confirm it (no-op).
  const [resolvedTaxonomy, setResolvedTaxonomy] =
    useState<CustomRoleTaxonomySelection | null>(null);

  const activeFilterMap = isCompanyPage
    ? filterState.groupMap.get(primaryFilterId)
    : undefined;
  const roleTaxonomyFilterState = activeFilterMap?.get(
    FilterId.ROLE_TAXONOMY
  ) as SingleValueFilter<CustomRoleTaxonomySelection> | undefined;

  // Resolved when the current filterState taxonomy matches what we resolved to.
  const isResolved =
    !isCompanyPage ||
    (resolvedTaxonomy !== null &&
      roleTaxonomyFilterState?.type === FilterType.VALUE &&
      isEqual(resolvedTaxonomy, roleTaxonomyFilterState.value));

  // Reset resolution when company selections change, since we need to
  // re-evaluate the adaptive taxonomy for the new companies.
  const companyFilterState = activeFilterMap?.get(FilterId.COMPANY);
  const companySelectionsKey =
    companyFilterState?.type === FilterType.TREE
      ? companyFilterState.selections.map((s) => s.id).join(',')
      : '';
  const prevCompanyKeyRef = useRef(companySelectionsKey);
  useEffect(() => {
    if (companySelectionsKey === prevCompanyKeyRef.current) {
      return;
    }

    prevCompanyKeyRef.current = companySelectionsKey;
    setResolvedTaxonomy(null);
  }, [companySelectionsKey]);

  const setResolvedTaxonomyIfChanged = (
    taxonomy: CustomRoleTaxonomySelection
  ) => {
    setResolvedTaxonomy((previous) => {
      if (previous !== null && isEqual(previous, taxonomy)) {
        return previous;
      }

      return taxonomy;
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (!isCompanyPage || !primaryFilterId || !filterState.activeKey) {
      return;
    }

    if (isScreenerV2Page(revelioPageId)) {
      // Screeners don't use adaptive taxonomy — resolve immediately.
      // Use the filter value if available, otherwise signal resolved
      // with a default so data providers are not permanently paused.
      setResolvedTaxonomyIfChanged(
        roleTaxonomyFilterState?.value ?? { default: true }
      );
      return;
    }

    if (filterState.activeKey !== primaryFilterId) {
      return;
    }

    const roleFilterState = activeFilterMap?.get(FilterId.ROLE);

    const allStatesSet =
      !!companyFilterState &&
      companyFilterState.type === FilterType.TREE &&
      !!roleFilterState &&
      !!roleTaxonomyFilterState &&
      roleTaxonomyFilterState.type === FilterType.VALUE;
    if (!allStatesSet) {
      return;
    }

    // If the user has an explicit Company taxonomy preference (non-adaptive),
    // treat it like a user-set taxonomy and never override it via adaptive logic.
    const companyPrefs = PreferenceBrowserStorage.get(PreferenceScope.COMPANY);
    const rolePref = companyPrefs[UserPreferenceKey.Taxonomies]?.role;

    if (rolePref && rolePref !== 'adaptive') {
      const explicitPreference = rolePref as CustomRoleTaxonomySelection;
      const preferenceValue: CustomRoleTaxonomySelection = {
        ...explicitPreference,
        version:
          explicitPreference.version ?? roleTaxonomyFilterState.value.version,
      };

      if (
        roleTaxonomyFilterState.isUserSubmitted !== true ||
        !isEqual(roleTaxonomyFilterState.value, preferenceValue)
      ) {
        dispatch({
          type: 'UPDATE_FILTER',
          filter: {
            ...roleTaxonomyFilterState,
            value: preferenceValue,
            isUserSubmitted: true,
          },
        });
      }
      setResolvedTaxonomyIfChanged(preferenceValue);
      return;
    }

    // NOTE: we intentionally don't early-return on
    // `roleTaxonomyFilterState.isUserSubmitted` here. At this point we've
    // ruled out a concrete saved/session COMPANY-scope preference (the
    // branch above), so the user's effective preference is adaptive (or
    // unset) — which means any `isUserSubmitted: true` on the filter is
    // almost certainly stale from another scope's preference sync (e.g.
    // the user visited a ROLE page, whose pref sync wrote
    // `{ default: true, isUserSubmitted: true }`, and that state is now
    // leaking into this COMPANY page). Respecting it would lock adaptive
    // out of re-resolving for the current company selection. Dropping
    // through to the adaptive logic below lets the hook reset the flag
    // and dispatch the right value.

    // For company pages, we need rics_k400 to compute adaptive taxonomy.
    // Don't signal resolution until we have the data.
    if (!selectionLists?.rics_k400?.length) {
      return;
    }

    // If roles are explicitly selected, keep the current taxonomy as-is.
    if (
      roleFilterState.type === FilterType.TREE &&
      roleFilterState.selections.length > 0
    ) {
      setResolvedTaxonomyIfChanged(roleTaxonomyFilterState.value);
      return;
    }

    const roleTaxonomyValue: CustomRoleTaxonomySelection = {
      ...getRoleTaxonomyValue(companyFilterState, selectionLists.rics_k400),
      version: roleTaxonomyFilterState.value.version,
    };

    if (!isEqual(roleTaxonomyValue, roleTaxonomyFilterState.value)) {
      dispatch({
        type: 'UPDATE_FILTER',
        filter: {
          ...roleTaxonomyFilterState,
          value: roleTaxonomyValue,
          isUserSubmitted: false,
        },
      });
    }
    setResolvedTaxonomyIfChanged(roleTaxonomyValue);
  }, [
    isCompanyPage,
    primaryFilterId,
    revelioPageId,
    filterState,
    dispatch,
    selectionLists,
    activeFilterMap,
    companyFilterState,
    roleTaxonomyFilterState,
  ]);

  return { isResolved };
};

// Lightweight wrapper that extracts the relevant selections from the company filter
// and delegates the heavy-lifting to `getAdaptiveRoleTaxonomyValue`.
export function getRoleTaxonomyValue(
  companyFilter: TreeFilter,
  ricsK400List: SelectionList<'rics_k400'>
): CustomRoleTaxonomySelection {
  const ricsK400Ids = companyFilter.selections
    .filter(({ levelId }) => levelId === 'company' || levelId === 'rics_k50')
    .map((selection) => selection.id.split('_')?.[1] ?? null)
    .filter((d): d is string => d !== null)
    .filter((d) => Number(d) <= 400);

  const ricsK10Ids = companyFilter.selections
    .map((selection) => selection.id.split('_')?.[0] ?? null)
    .filter((d): d is string => d !== null)
    .filter((d) => Number(d) <= 10);

  const ricsK50Ids = companyFilter.selections
    .filter(({ levelId }) => levelId === 'rics_k50')
    .map((selection) => selection.id.split('_')?.[1] ?? selection.id)
    .filter((d): d is string => d !== null)
    .filter((d) => Number(d) <= 50);

  const taxonomyId = getAdaptiveRoleTaxonomyValue({
    ricsK400List,
    ricsSelections: {
      rics_k10: ricsK10Ids,
      rics_k50: ricsK50Ids,
      rics_k400: ricsK400Ids,
    },
  });

  if (!taxonomyId || taxonomyId.includes('default')) {
    return { default: true };
  }
  if (taxonomyId.includes('rics_k10')) {
    const id = taxonomyId.split('custom_role_rics_k10_')[1];
    if (id === '0') return { default: true };
    return id ? { ricsK10Id: id } : { default: true };
  }
  if (taxonomyId.includes('rics_k50')) {
    const id = taxonomyId.split('custom_role_rics_k50_')[1];
    if (id === '0') return { default: true };
    return id ? { ricsK50Id: id } : { default: true };
  }
  return { default: true };
}
